import { Injectable } from "@angular/core";
import { ApplicationHttpService } from "../../../core/services/application-http.service";
import { BehaviorSubject, catchError, distinctUntilChanged, forkJoin, map, Observable, of, Subject, Subscription, switchMap, tap, timer } from "rxjs";
import { AttachMeta } from "../../../core/models/attach.model";
import { OverviewModel } from "../../../shared/models/overview.model";
import { CommunicationContent, Conversation, ConversationApplicationRequest, ConversationApplicationsResponse, ConversationRequest, ConversationResponse, Message, MessagesResponse, NewMessage } from "../models/communications.model";

const COMMUNICATION_SERVICE_URL = 'conversation';
const UPLOAD_URL = `document/upload?code=CONVERSATION_ATTACHMENT_DOCUMENT`;


@Injectable()
export class CommunicationService {
    communicationContent$ = new BehaviorSubject<CommunicationContent>(CommunicationContent.MAIN_REQUESTS);
    selectedRequestChat$ = new BehaviorSubject<any>(null);
    //@ts-ignore
    userId: number;

    public readonly displayModalSubject = new Subject<number>();
    public readonly displayModal$: Observable<number> = this.displayModalSubject.asObservable();
    private readonly unreadCountSubject = new BehaviorSubject<number>(0);
    public readonly unreadCount$ = this.unreadCountSubject.asObservable();
    private subs?: Subscription;


    constructor(private http: ApplicationHttpService, 
        // private storage: StorageService
    ) {}

    /**
     * Http requests
     * */

    getCommunicationRequestChats(request: ConversationRequest): Observable<ConversationResponse> {
        return this.http.get<ConversationResponse>(`${COMMUNICATION_SERVICE_URL}/page${request.getParams()}`);
    }

    getApplicationsByTypeId(typeId: number): Observable<any> {
        return of(
            // mockApplications.filter(item => item.applicationTypeId == typeId)
        );
    }

    getMessagesByChat(applicationId: string): Observable<MessagesResponse> {
        return this.http.post<MessagesResponse>(`${COMMUNICATION_SERVICE_URL}/open/${applicationId}`, null);
    }

    getUnreadMessages(): Observable<number> {
        return this.http.get<number>(`${COMMUNICATION_SERVICE_URL}/unread`);
    }

    getApplicationList(request: ConversationApplicationRequest): Observable<ConversationApplicationsResponse> {
        return this.http.get<ConversationApplicationsResponse>(`${COMMUNICATION_SERVICE_URL}/available-applications${request.getParams()}`);
    }

    sendMessage(message: NewMessage): Observable<MessagesResponse> {
        return this.http.post<MessagesResponse>(`${COMMUNICATION_SERVICE_URL}/send-message`, message);
    }

    uploadDocument(file: File): Observable<AttachMeta> {
        const form = new FormData();
        form.append('file', file, file.name);
        return this.http.post<AttachMeta>(UPLOAD_URL, form);
    }

    uploadDocuments(files: File[]): Observable<AttachMeta[]> {
        if (!files?.length) return of([]);
        return forkJoin(files.map(f => this.uploadDocument(f)));
    }

    downloadDocument(documentId: number): Observable<Blob> {
        return this.http.getFile(`document/download/${documentId}`);
    }

    checkForAvailabilitySendMessage(applicationId: string): Observable<boolean> {
        return this.http.get<boolean>(`conversation/send-message-check/${applicationId}`);
    }

    startUnreadPolling(intervalMs: number = 30000): void {
        if (this.subs) return; 

        this.subs = timer(0, intervalMs).pipe(
            switchMap(() =>
                this.getUnreadMessages().pipe(
                catchError(() => of(null)) 
                )
            ),
            map(v => (typeof v === 'number' ? v : this.unreadCountSubject.value)),
            distinctUntilChanged()
        ).subscribe(count => this.unreadCountSubject.next(count));
    }

    /**
     * Getter & Setter
     * */
    get communicationContent() {
        return this.communicationContent$.getValue();
    }

    set communicationContent(content) {
        this.communicationContent$.next(content);
    }

    get selectedRequestChat() {
        return this.selectedRequestChat$.getValue();
    }

    set selectedRequestChat(requestChat) {
        this.selectedRequestChat$.next(requestChat);
    }
}